import React, { useState, useEffect, startTransition } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import css from './RoleBasedLayout.module.css';
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import { SideBar } from "../../components";
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { authActions } from '../../store/slices/auth-slices';
import { getConfig } from '../../config';

/**
 * RoleBasedLayout Component
 * 
 * A layout component that provides role-based access control.
 * It wraps the application content and ensures users have appropriate permissions.
 * 
 * @param {Object} props
 * @param {string[]} [props.allowedRoles] - Array of roles that can access this layout
 * @param {string} [props.minimumRole] - Minimum role required (uses role hierarchy)
 * @param {boolean} [props.showAccessDenied] - Whether to show access denied page or redirect
 * @param {string} [props.redirectTo] - Where to redirect if access is denied
 * @param {React.ReactNode} [props.accessDeniedComponent] - Custom access denied component
 */
const RoleBasedLayout = ({ 
  allowedRoles = null,
  minimumRole = null,
  showAccessDenied = true,
  redirectTo = '/login',
  accessDeniedComponent = null,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = getConfig();
  const {
    userRole,
    hasAnyRole,
    meetsMinimumRole,
    isValidRole,
  } = useRoleAccess();

  const handleLogout = () => {
    dispatch(authActions.logout()).unwrap().then(() => {
      startTransition(() => {
        navigate("/login");
      });
    });
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  // Check mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check role-based access
  useEffect(() => {
    if (!userRole || !isValidRole) {
      setHasAccess(false);
      return;
    }

    // If no restrictions, allow access
    if (!allowedRoles && !minimumRole) {
      setHasAccess(true);
      return;
    }

    // Check against allowed roles
    if (allowedRoles && Array.isArray(allowedRoles)) {
      setHasAccess(hasAnyRole(allowedRoles));
      return;
    }

    // Check against minimum role
    if (minimumRole) {
      setHasAccess(meetsMinimumRole(minimumRole));
      return;
    }

    setHasAccess(true);
  }, [userRole, allowedRoles, minimumRole, hasAnyRole, meetsMinimumRole, isValidRole]);

  // Handle access denial
  useEffect(() => {
    if (!hasAccess && !showAccessDenied && redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [hasAccess, showAccessDenied, redirectTo, navigate]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Show access denied page
  if (!hasAccess && showAccessDenied) {
    if (accessDeniedComponent) {
      return accessDeniedComponent;
    }

    return (
      <div className={css.accessDeniedContainer}>
        <div className={css.accessDeniedContent}>
          <div className={css.logoArea} style={{ display: "flex", justifyContent: "center", marginBottom: "1.75rem" }}>
            <img src={config.logo} alt={config.title} style={{ height: "44px", objectFit: "contain", maxWidth: "160px" }} />
          </div>

          <div className={css.accessDeniedIcon}>
            <div className={css.accessDeniedIconCircle}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
          </div>

          <h1 className={css.accessDeniedTitle}>Access Denied</h1>
          <p className={css.accessDeniedMessage}>
            You don't have permission to access this section.
            Contact your administrator if you believe this is a mistake.
          </p>

          <div className={css.accessDeniedRolePills}>
           
            {allowedRoles && (
              <span className={css.accessDeniedRole}>
                <span className={css.accessDeniedRoleLabel}>Required:</span>
                <span className={css.accessDeniedRoleValue}>{allowedRoles.join(', ')}</span>
              </span>
            )}
          </div>

          <div className={css.accessDeniedActions}>
            <button className={css.accessDeniedLogoutBtn} onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking access
  if (!hasAccess && !showAccessDenied) {
    return (
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: "1rem", background: "#f1f5f9", fontFamily: "Arial, sans-serif", fontSize: "0.9375rem", color: "#64748b" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #e2e8f0", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        Checking permissions...
      </div>
    );
  }

  // Render layout with access granted
  return (
    <section className={css.wrap}>
      {isMobile && (
        <MobileHeader 
          onMenuToggle={toggleMobileMenu}
          isMenuOpen={isMobileMenuOpen}
        />
      )}
      
      {isMobile && isMobileMenuOpen && (
        <div 
          className={`${css.overlay} ${isMobileMenuOpen ? css.visible : ''}`}
          onClick={closeMobileMenu}
        />
      )}

      <aside className={`${css.menuBlock} ${isMobileMenuOpen ? css.open : ''}`}>
        <SideBar onMenuClose={closeMobileMenu} isMobile={isMobile} />
      </aside>

      <section className={css.outletContainer}>
        <Outlet/>
      </section>
    </section>
  );
};

export { RoleBasedLayout };
